
import React from 'react';
import { sportsFacilities } from '../../../mocks/parks';

/**
 * Returns a safe Google Maps URL for the given address.
 * If the address is missing or empty, returns a placeholder (`#`).
 */
function getMapsLink(address?: string): string {
  if (!address) return '#';
  try {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      address
    )}`;
  } catch {
    // In the unlikely event encodeURIComponent throws
    return '#';
  }
}

/**
 * Handles image load errors by swapping to a placeholder image.
 */
function handleImageError(
  e: React.SyntheticEvent<HTMLImageElement, Event>
): void {
  const placeholder =
    'https://via.placeholder.com/340x192?text=Image+unavailable';
  if (e.currentTarget.src !== placeholder) {
    e.currentTarget.src = placeholder;
  }
}

export default function SportsSection() {
  return (
    <section id="sports" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="inline-flex items-center gap-2 bg-teal-100 px-3 py-1 rounded-full mb-3">
              <i className="ri-trophy-line text-teal-600 text-sm"></i>
              <span className="text-teal-700 text-xs font-semibold">
                {sportsFacilities.length} Facilities
              </span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Sports Facilities
            </h2>
            <p className="text-gray-500">
              Courts, fields, pools, and more for athletes of every level
            </p>
          </div>
        </div>

        {/* Horizontal scroll cards */}
        <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide -mx-6 px-6">
          {sportsFacilities.map((facility) => (
            <div
              key={facility.id}
              className="min-w-[340px] max-w-[340px] bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 group flex-shrink-0"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={facility.image}
                  alt={facility.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  onError={handleImageError}
                />
                <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                  <span className="text-xs font-semibold text-gray-800">
                    {facility.rating}
                  </span>
                </div>
              </div>

              <div className="p-5">
                <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  {facility.name}
                </h3>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {facility.sports.map((sport) => (
                    <span
                      key={sport}
                      className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs rounded-full font-medium"
                    >
                      {sport}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                  {facility.description}
                </p>

                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-time-line text-teal-500"></i>
                    <span className="text-xs">{facility.hours}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <a
                    href={getMapsLink(facility.address)}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-1 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-navigation-line"></i>
                    Directions
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
